import React, { useEffect, useState } from "react";
import { Row, Col, Form, Badge } from "react-bootstrap";
import { getChats, getPlayerChats } from "../../Api/Chats/Chats";
import { SingleChat } from "../../Api/Chats/models";
import { LiveChat } from "./LiveChat";
import { useSocket } from "../../../../hooks/useSocket";
import { useAdminInfo } from "../../../../hooks/useAdminInfo";
import { NonSelectedChat } from "./NonSelectedChat";
import { Title } from "../../../../Templates/Title";
import { IoChatbubbleEllipsesSharp, IoEye, IoEyeOff } from "react-icons/io5";
import { InitializeChat } from "./InitializeChat";
import cookies from "js-cookie";
import { Home } from "../Home/Home";

export const Chat: React.FC = () => {
  const { socket } = useSocket();
  const { adminInfo } = useAdminInfo();
  const [chats, setChats] = useState<SingleChat[]>([]);
  const [selectedChat, setSelectedChat] = useState<SingleChat | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const [showHome, setShowHome] = useState(true);
  const [isNewChat, setIsNewChat] = useState(false);
  const [newChatPlayerData, setNewChatPlayerData] = useState<any>(null);

  const fetchChats = async (pageNumber: number) => {
    try {
      setLoading(true);
      const res = await getChats({
        page: pageNumber,
        searchValue: searchValue
      });

      if (res?.chats) {
        if (pageNumber === 1) {
          setChats(res.chats);
        } else {
          setChats(prev => [...prev, ...res.chats]);
        }
        setHasMore(res.chats.length > 0);
      }
    } finally {
      setLoading(false);
    }
  }

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight * 1.5 && !loading && hasMore) {
      setPage(prev => prev + 1);
    }
  };

  const handleSelectChat = (chat: SingleChat) => {
    setSelectedChat(chat);
    if (chat.seenByUser) return;

    if (socket) {
      socket.emit(`seen-chat`, {
        ChatId: chat.id,
        OwnerId: adminInfo?.id,
        role: 'user',
      });
    }

    // Update chat locally
    setChats(prev => prev.map(c => c.id === chat.id ? { ...c, seenByUser: true } : c));
  }

  const cleanSearch = () => {
    setSearchValue('');
  }

  const fetchPlayerChats = async (playerId: string) => {
    const res = await getPlayerChats({ playerId })
    if (res?.success && res?.chat?.id) {
      setSelectedChat(res.chat)
    } else if (res?.success && res?.chat === null) {
      setIsNewChat(true)
      setNewChatPlayerData(res.player)
    }
  }

  useEffect(() => {
    const playerId = new URLSearchParams(window.location.search).get('playerId');
    if (playerId) {
      fetchPlayerChats(playerId)
    }
  }, []);

  useEffect(() => {
    fetchChats(page);
  }, [page]);

  useEffect(() => {
    if (searchValue === '') {
      setPage(1);
      fetchChats(1);
    }
  }, [searchValue]);

  useEffect(() => {
    if (socket) {
      socket.on(`update-chats-${adminInfo?.id}`, () => {
        setPage(1);
        fetchChats(1);
      });
    }
    return () => {
      if (socket) {
        socket.off(`update-chats-${adminInfo?.id}`)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, adminInfo]);


  return (
    <div className="mt-4 rounded text-light w-full pb-5" style={{ height: 'calc(100vh - 100px)' }}>

      <div className="d-flex justify-content-between align-items-end">
        <Title
          title="Chats"
          icon={<IoChatbubbleEllipsesSharp size={30} />}
          subtitle="Chats con jugadores"
        />
        <div className="d-flex align-items-center mx-2">
          <span className="me-2 text-white">
            <Badge
              bg={showHome ? "secondary" : "primaryy"}
              onClick={() => setShowHome(!showHome)}
              role="button"
              className=" d-flex align-items-center gap-1"
            >
              {showHome ? "Ocultar panel" : "Mostrar panel"}
              {showHome ? <IoEyeOff size={15} /> : <IoEye size={15} />}
            </Badge>
          </span>
        </div>
      </div>
      <hr />
      <Row className="p-2 rounded-3">
        <Col md={2} className="my-0 d-flex flex-column">
          <div
            style={{ overflowY: 'auto', height: 'calc(95vh - 100px)' }}
            className="px-1"
            onScroll={handleScroll}
          >

            <div className="d-flex justify-content-center align-items-center mb-2 gap-2 w-100">
              <div className="position-relative w-100">
                <input type="text"
                  className="form-control w-100"
                  style={{ paddingRight: '25px' }}
                  placeholder="Buscar jugador"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />
                {searchValue &&
                  <div className="position-absolute" style={{ right: '10px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer' }}>
                    <span
                      className="text-secondary"
                      onClick={cleanSearch}
                    >
                      x
                    </span>
                  </div>
                }
              </div>
              <button className="btn btn-primary" onClick={() => fetchChats(1)}>Buscar</button>
            </div>

            {chats?.map((chat, idx) => (
              <div key={idx} role='button'
                className={`position-relative rounded-3 mb-2 p-2 border border-secondary align-items-center ${selectedChat?.id === chat.id ? "bg-dark" : ""} ${chat?.seen ? "border-primaryy" : ""}`}
                onClick={() => { handleSelectChat(chat) }}>
                <div className="d-flex flex-column position-relative">
                  <span className="text-primaryy"><b>{chat.Player.platformUser.slice(0, 20) + (chat.Player.platformUser.length > 20 ? "..." : "")}</b></span>
                  <span>
                    {chat?.ChatMessages[0]?.creatorType === 'player' ?
                      <span className="text-white">{chat?.ChatMessages[0]?.message?.slice(0, 15) + (chat?.ChatMessages[0]?.message?.length > 15 ? "..." : "")}</span>
                      :
                      <span className="text-secondary">Tú: {chat?.ChatMessages[0]?.message?.slice(0, 15) + (chat?.ChatMessages[0]?.message?.length > 15 ? "..." : "")}</span>
                    }
                  </span>
                  <div className="d-flex justify-content-end position-absolute" style={{ top: 0, right: 0 }}>
                    <span className="text-secondary text-xs">{new Date(chat?.ChatMessages[0]?.updatedAt).toLocaleString()}</span>
                  </div>
                </div>
                {!chat.seenByUser && selectedChat?.id !== chat.id && <div className="position-absolute" style={{ bottom: '10px', right: '10px' }}>
                  <span className="text-white text-xs bg-danger rounded-circle d-flex align-items-center justify-content-center"
                    style={{ fontSize: '10px', width: '20px', height: '20px' }}>
                    1
                  </span>
                </div>
                }
              </div>
            ))}

            {!chats.length && !loading && <div className="text-center py-2 border border-secondary rounded-3">No tienes chats</div>}
            {loading && <div className="text-center py-2">Cargando...</div>}
          </div>
        </Col>
        <Col md={showHome ? 6 : 10}>
          <Row>
            {selectedChat && <LiveChat chat={selectedChat} />}
            {!selectedChat && !isNewChat && <NonSelectedChat />}
            {isNewChat && <InitializeChat playerData={newChatPlayerData} setSelectedChat={setSelectedChat} setIsNewChat={setIsNewChat} />}
          </Row>
        </Col>
        {showHome && <Col md={4} style={{ overflowY: 'auto', height: 'calc(95vh - 100px)' }}>
          <Home embed={true} />
        </Col>
        }
      </Row>
    </div >
  );
};
